// React Query hooks for the MarcusAI Workbench (Capability 3, MW-4). Mirrors the
// VB data layer: per-request token (avoids the cold-load 401 race), workspace
// gating via the shared `useVbWorkspaceScope`, and a `{ ws }` suffix on every
// key so switching workspaces gets its own cache entries.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { workbenchClient } from "@/services/workbench/workbenchClient";
import { useAuth } from "@/context/AuthContext";
import { useVbWorkspaceScope } from "@/hooks/vb/useBuyers";
import type {
  WorkbenchQueue,
  WorkbenchTaskPatch,
  WorkbenchTaskUpdate,
} from "@/types/workbench";

/** Query-key factory (separate namespace from `vbKeys`). */
export const workbenchKeys = {
  all: ["workbench"] as const,
  queue: (companyId: number) => [...workbenchKeys.all, "queue", companyId] as const,
};

/** GET the fix queue for a company's latest paid report.
 *
 *  Gated on `token` + `wsReady` (same reasons as the VB reads) and on `enabled`
 *  so the caller can stay disabled for free plans (the endpoint 403s). */
export function useWorkbenchQueue(companyId: number | undefined, enabled = true) {
  const { token } = useAuth();
  const { wsKey, wsReady } = useVbWorkspaceScope();
  const valid = companyId != null && Number.isInteger(companyId) && companyId > 0;
  return useQuery<WorkbenchQueue>({
    queryKey: [...workbenchKeys.queue(companyId ?? -1), { ws: wsKey }],
    queryFn: () => workbenchClient.getQueue(companyId as number, token),
    enabled: enabled && valid && !!token && wsReady,
    staleTime: 60 * 1000,
    // 403 (free) / 404 (no paid report for this company) are deterministic —
    // don't retry them. The sidebar badge calls this for the active company,
    // which often has no workbench, so retrying 404s would be wasteful.
    retry: (failureCount, err) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}

/** Merge an MW-2 task-update response back into a cached queue: replace the one
 *  fix's `steps` + `task`, leaving everything else (and ordering) untouched. The
 *  queue view derives progress/grouping from `fixes`, so this drives the UI. */
function patchQueueWithUpdate(
  queue: WorkbenchQueue | undefined,
  update: WorkbenchTaskUpdate,
): WorkbenchQueue | undefined {
  if (!queue) return queue;
  return {
    ...queue,
    fixes: queue.fixes.map((fix) =>
      fix.signal_key === update.signal_key
        ? { ...fix, steps: update.steps, task: update.task }
        : fix,
    ),
  };
}

/** PATCH a fix's task state (status / notes / step ticks). On success the server
 *  echoes the merged steps + task, which we write straight into the cached queue
 *  so the pane + queue re-rank without a refetch. `companyId` is fixed per view.
 *
 *  Concurrent per-step ticks are safe: each PATCH sends only the toggled index
 *  (`step_state` merges server-side), and we reconcile with the authoritative
 *  response, so a slower request can't clobber a newer tick's field. */
export function useUpdateTask(companyId: number) {
  const { token } = useAuth();
  const { wsKey } = useVbWorkspaceScope();
  const qc = useQueryClient();
  const key = [...workbenchKeys.queue(companyId), { ws: wsKey }];
  return useMutation({
    mutationFn: ({ signalKey, patch }: { signalKey: number; patch: WorkbenchTaskPatch }) =>
      workbenchClient.updateTask(companyId, signalKey, patch, token),
    onSuccess: (update) => {
      qc.setQueryData<WorkbenchQueue>(key, (prev) => patchQueueWithUpdate(prev, update));
    },
  });
}
